import { Bell } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useUserSocket } from "@/hooks/useSockets";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/intl";
import { timeAgo } from "@/components/bidding/BidHistory";

/*
 * In-app notification bell (public shell + dashboard).
 * Real-time via the private user WebSocket channel; REST for history + read state.
 */

const notificationText = (n, t, locale) => {
  const p = n.payload || {};
  const amount = p.amount_cents ? formatCurrency(p.amount_cents, p.currency || "EUR", locale) : "";
  switch (n.type) {
    case "outbid":
      return p.reason === "proxy_defense"
        ? t("ntf.outbidProxy", { name: p.horse_name || "", amount })
        : t("ntf.outbid", { name: p.horse_name || "", amount });
    case "won":
      return t("ntf.won", { name: p.horse_name || "", amount });
    case "lost":
      return t("ntf.lost", { name: p.horse_name || "" });
    case "auction_live":
      return t("ntf.auction_live");
    case "registration_request":
      return t("ntf.registration_request", { name: p.user_name || "" });
    case "registration_approved":
      return t("ntf.registration_approved", { paddle: p.paddle_number || "" });
    case "registration_declined":
      return t("ntf.registration_declined");
    case "lot_closed":
      return t("ntf.lot_closed", { name: p.horse_name || "" });
    default:
      return n.type;
  }
};

const targetFor = (n, role) => {
  const p = n.payload || {};
  if (n.type === "registration_request") return "/dashboard/buyers";
  if (n.type === "won") return "/account/won";
  if (n.type === "lot_closed" && role !== "buyer") return "/dashboard/events";
  if (p.event_id && p.lot_id) return null; // handled via slug below when present
  return "/account/bids";
};

export const NotificationsBell = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(() => {
    api.get("/notifications").then(({ data }) => {
      setItems(data.items);
      setUnread(data.unread);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  useUserSocket(
    useCallback((msg) => {
      if (msg.type === "notification") {
        setItems((prev) => [msg.notification, ...prev].slice(0, 50));
        setUnread((u) => u + 1);
      }
    }, []),
    Boolean(user)
  );

  if (!user) return null;

  const openChange = (open) => {
    if (open && unread > 0) {
      api.post("/notifications/read-all").then(() => setUnread(0)).catch(() => {});
    }
  };

  return (
    <DropdownMenu onOpenChange={openChange}>
      <DropdownMenuTrigger
        data-testid="notifications-bell"
        aria-label={t("ntf.title")}
        className="relative inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-black/5 transition-colors duration-200"
      >
        <Bell className="w-4 h-4 text-[#0A0A0A]" aria-hidden="true" />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-dot"
            className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#B42318] text-white text-[9px] font-bold inline-flex items-center justify-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-xl border-black/5 shadow-[0_16px_40px_rgb(0,0,0,0.08)] w-80 p-0">
        <div className="px-4 py-3 border-b border-black/5 text-sm font-semibold text-[#0A0A0A]">{t("ntf.title")}</div>
        <div className="max-h-96 overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 ? (
            <p className="px-4 py-8 text-sm text-[#737373] text-center">{t("acct.notificationsEmptyTitle")}</p>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                data-testid={`notification-item-${n.type}`}
                onClick={() => {
                  const to = targetFor(n, user.role);
                  if (to) navigate(to);
                }}
                className={`w-full text-left px-4 py-3 border-b border-black/[0.04] hover:bg-black/[0.03] transition-colors ${!n.read ? "bg-[#1D4D3B]/[0.04]" : ""}`}
              >
                <p className="text-sm text-[#0A0A0A] leading-snug">{notificationText(n, t, locale)}</p>
                <p className="text-xs text-[#737373] mt-0.5">{timeAgo(n.created_at, t)}</p>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
